/**
 * Gothic letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const gothicLettersPack: ToilePack = {
    slug: 'gothic-letters',
    category: 'illustration',
    name: 'Gothic letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'textura', label: 'Textura', subject: 'a chart of the textura lowercase "a" to "z" in four rows, dense upright strokes with diamond feet, and the capitals "A" to "Z" beneath'},
        {key: 'fraktur', label: 'Fraktur', subject: 'a chart of the fraktur capitals "A" to "Z" in six rows, broken curves, small elephant-trunk hairlines and a slight lean'},
        {key: 'word', label: 'A word', subject: 'the word "Strength" in large gothic blackletter across the page, tight letter spacing and a single flourish off the S'},
    ],
    styles: [
        {key: 'solid', label: 'Solid black', style: 'gothic blackletter in solid black broad-pen strokes with fine hairlines', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'outline', label: 'Outline', style: 'gothic blackletter drawn as a thin outline only, the strokes left white inside', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
        {key: 'ornate', label: 'Ornate', style: 'gothic blackletter with decorated capitals, pointed finials and small thorny flourishes', palette: 'black ink only', framing: 'the chart filling the page inside an even white margin'},
    ],
    directions: ['a name', 'a dragon', 'a cross'],
    directionHint: 'try: a name',
    seo: {
        title: 'Gothic Letters: Textura, Fraktur, a Blackletter Word',
        metaDescription: 'Nine gothic blackletter sheets: textura lowercase, fraktur capitals and the word Strength, in solid, outline and ornate styles.',
        h1: 'Gothic letters: textura and fraktur alphabets, a blackletter word',
        tagline: 'Blackletter alphabets and a word, in solid, outline and ornate forms.',
        intro: 'Gothic letters are blackletter alphabets for tattoos, titles and calligraphy study. The nine sheets show the textura lowercase in four rows, the fraktur capitals with broken curves and small hairlines, and the word Strength in large blackletter. Each set comes as solid black broad strokes, as a thin outline with white inside, or with decorated capitals and pointed finials. Ask for a name, a dragon or a cross as the subject.',
        useCases: [
            'a calligraphy study sheet',
            'a title for a poster or a book',
            'a tattoo design reference',
            'a framed word or name',
        ],
        faq: [
            {q: 'What is the difference between textura and fraktur?', a: 'Textura has dense, upright strokes with diamond feet. Fraktur has broken curves and small elephant-trunk hairlines.'},
            {q: 'Are the alphabets complete?', a: 'The textura sheet shows lowercase a to z. The fraktur sheet shows the capitals A to Z.'},
            {q: 'What are the three styles?', a: 'Solid black has broad pen strokes with fine hairlines. Outline is a thin line with the strokes left white. Ornate adds decorated capitals, pointed finials and small flourishes.'},
            {q: 'Can I get a name in blackletter?', a: 'Yes. Ask for a name, a dragon or a cross, and the blackletter is set to that subject.'},
        ],
    },
}
